/**
 * LegalLinks — Privacy / Terms / Support links exposed in-app.
 *
 * Store-compliance requirement: every app that ships to Apple / Google
 * must expose a Privacy Policy from within the app (Apple Guideline 5.1)
 * and, for auto-renewable subscriptions, a Terms of Use (Apple 3.1.2).
 *
 * Community Guidelines were removed alongside The Collective on
 * 2026-07-25 — the app no longer hosts user-generated public content,
 * so Apple 1.2 no longer applies.
 *
 * Navigation: uses SPA routing (React Router) so the browser's back
 * stack is preserved. That way the target pages can pop back to
 * Settings via the shared `useSmartBack` hook, on web AND inside the
 * Capacitor WebView on iOS/Android. Using an external system-browser
 * (Capacitor.Browser) here would produce an isolated tab with no
 * history — the previous implementation dropped the user on Landing
 * (which prominently shows "Sign In", and was misread as the login
 * screen) whenever they hit the in-page back button. Fixed as of
 * 2026-07-29 (Iteration 47).
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, FileText, LifeBuoy } from 'lucide-react';

const LegalLinks = () => {
  const navigate = useNavigate();

  const items = [
    { id: 'privacy', label: 'Privacy Policy', icon: Shield, to: '/privacy' },
    { id: 'terms', label: 'Terms of Use', icon: FileText, to: '/terms' },
    { id: 'support', label: 'Support', icon: LifeBuoy, to: '/support' },
  ];

  return (
    <div
      className="bg-[#252528] border border-white/5 rounded-[24px] overflow-hidden"
      data-testid="legal-links"
    >
      {items.map((item, i) => {
        const Icon = item.icon;
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => navigate(item.to)}
            className={`w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-white/5 transition-colors ${
              i > 0 ? 'border-t border-white/5' : ''
            }`}
            data-testid={`legal-link-${item.id}`}
            aria-label={item.label}
          >
            <Icon className="w-4 h-4 text-[#8BAA9E]" strokeWidth={1.75} />
            <span className="flex-1 text-[#DCD3CA] font-sans text-sm">{item.label}</span>
            <span className="text-[#7A7670] text-xs font-sans" aria-hidden="true">›</span>
          </button>
        );
      })}
    </div>
  );
};

export default LegalLinks;
